import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import api from '../services/api';
import {
  IconNavCamera,
  IconNavFridge,
  IconInfo,
} from '../components/icons';
import './Inventar.css';

const SORT_DRINGLICHKEIT = 'dringlichkeit';
const SORT_AZ            = 'az';
const SORT_BESTAND       = 'bestand';

function getUrgencyGroup(days) {
  if (days <= 1) return 'kritisch';
  if (days <= 3) return 'bald';
  return 'ok';
}

function getPillProps(days) {
  if (days <= 0) return { cls: 'pill prio-pill-today', text: 'HEUTE!' };
  if (days === 1) return { cls: 'pill prio-pill-today', text: 'MORGEN' };
  if (days <= 3) return { cls: 'pill pill-amber', text: `NOCH ${days}T` };
  return { cls: 'pill pill-green', text: `NOCH ${days}T` };
}

function sortItems(items, sort) {
  const copy = [...items];
  if (sort === SORT_AZ) return copy.sort((a, b) => a.name.localeCompare(b.name, 'de'));
  if (sort === SORT_BESTAND) return copy.sort((a, b) => a.menge - b.menge || a.days_left - b.days_left);
  return copy.sort((a, b) => a.days_left - b.days_left);
}

function mengeNext(current) {
  if (current === 3) return 2;
  if (current === 2) return 1;
  return 3;
}

function MengeDots({ menge }) {
  return (
    <span className="inv-menge-dots">
      <span className={`inv-dot-m ${menge >= 1 ? 'inv-dot-m--filled' : ''}`} />
      <span className={`inv-dot-m ${menge >= 2 ? 'inv-dot-m--filled' : ''}`} />
      <span className={`inv-dot-m ${menge >= 3 ? 'inv-dot-m--filled' : ''}`} />
    </span>
  );
}

export default function Inventar() {
  const { setPageTitle } = useBreadcrumb();
  const [items, setItems]         = useState([]);
  const [lastScan, setLastScan]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [sort, setSort]           = useState(SORT_DRINGLICHKEIT);
  const [updating, setUpdating]   = useState(null); // item-id während PATCH

  useEffect(() => {
    setPageTitle('Mein Inventar');
  }, [setPageTitle]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getFridgeItems();
      setItems(data.items || []);
      setLastScan(data.last_scan || null);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMengeTap = useCallback(async (item) => {
    const next = mengeNext(item.menge);
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, menge: next } : i));
    setUpdating(item.id);
    try {
      await api.updateFridgeItemMenge(item.id, next);
    } catch {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, menge: item.menge } : i));
    } finally {
      setUpdating(null);
    }
  }, []);

  const sorted = sortItems(items, sort);

  const groups = sort === SORT_DRINGLICHKEIT
    ? [
        { key: 'kritisch', label: 'Heute & morgen', items: sorted.filter(i => getUrgencyGroup(i.days_left) === 'kritisch') },
        { key: 'bald',     label: '1 – 3 Tage',      items: sorted.filter(i => getUrgencyGroup(i.days_left) === 'bald') },
        { key: 'ok',       label: '4+ Tage',          items: sorted.filter(i => getUrgencyGroup(i.days_left) === 'ok') },
      ].filter(g => g.items.length > 0)
    : [{ key: 'all', label: null, items: sorted }];

  const lastScanText = lastScan
    ? `zuletzt gescannt ${new Date(lastScan).toLocaleDateString('de-AT', { day: 'numeric', month: 'long' })}`
    : null;

  return (
    <div className="inv-page fade-in">
      {/* page-header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Mein <em>Inventar</em></h1>
          {lastScanText && <p className="page-sub">{items.length} Lebensmittel · {lastScanText}</p>}
        </div>
        <div className="page-header-right">
          <Link to="/kuehlschrank" className="btn btn-primary btn-sm">
            <IconNavCamera size={14} /> Scannen
          </Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="inv-disclaimer">
        <IconInfo size={13} />
        Haltbarkeitsdaten basieren auf KI-Schätzwerten und können abweichen. Bitte prüfe das Mindesthaltbarkeitsdatum auf der Verpackung.
      </div>

      {loading ? (
        <div className="inv-loading"><span className="loader" /></div>
      ) : items.length === 0 ? (
        <div className="inv-empty">
          <div className="inv-empty-icon"><IconNavFridge size={48} /></div>
          <div className="inv-empty-title">Noch kein Scan vorhanden</div>
          <p className="inv-empty-sub">Scanne deinen Kühlschrank um das Inventar zu befüllen.</p>
          <Link to="/kuehlschrank" className="btn btn-primary" style={{ marginTop: 16 }}>
            <IconNavCamera size={14} /> Jetzt scannen
          </Link>
        </div>
      ) : (
        <>
          {/* Sort-Bar */}
          <div className="inv-sort-bar">
            <span className="inv-sort-label">Sortierung</span>
            <button className={`inv-sort-btn${sort === SORT_DRINGLICHKEIT ? ' active' : ''}`} onClick={() => setSort(SORT_DRINGLICHKEIT)}>Dringlichkeit</button>
            <button className={`inv-sort-btn${sort === SORT_AZ ? ' active' : ''}`} onClick={() => setSort(SORT_AZ)}>A – Z</button>
            <button className={`inv-sort-btn${sort === SORT_BESTAND ? ' active' : ''}`} onClick={() => setSort(SORT_BESTAND)}>Bestand</button>
            <span className="inv-item-count">{items.length} Items</span>
          </div>

          {/* Liste */}
          <div className="inv-list card">
            {groups.map((group, gi) => (
              <React.Fragment key={group.key}>
                {group.label && (
                  <div className="inv-group-label">{group.label}</div>
                )}
                {group.items.map((item, idx) => {
                  const { cls: pillCls, text: pillText } = getPillProps(item.days_left);
                  const dotCls = item.days_left <= 1 ? 'inv-amp--red'
                               : item.days_left <= 3 ? 'inv-amp--amber'
                               : 'inv-amp--green';
                  const isLast = gi === groups.length - 1 && idx === group.items.length - 1;
                  return (
                    <div key={item.id} className={`inv-row${isLast ? ' inv-row--last' : ''}`}>
                      <span className={`inv-amp ${dotCls}`} />
                      <span className="inv-name">{item.name}</span>
                      <button
                        className={`inv-menge-btn${updating === item.id ? ' inv-menge-btn--saving' : ''}`}
                        onClick={() => handleMengeTap(item)}
                        title="Bestand ändern — 1-Tap"
                        aria-label={`Bestand von ${item.name} ändern`}
                      >
                        <MengeDots menge={item.menge} />
                      </button>
                      <span className={pillCls} style={{ whiteSpace: 'nowrap' }}>{pillText}</span>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
